import { WebPageEvents } from '@griply/types';
import Typography from '@mui/material/Typography';
import { useCompleteSignupAndLogin, useSignupClaim } from 'features/user';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { LoginLogo } from 'shared/components/ui/login-logo';
import { PageLoader } from 'shared/components/ui/page-loader';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { QueryParamOptions, useQueryParam } from 'shared/lib/react-router-dom';
import { CompleteSignupFormFields } from 'shared/types/complete-signup-form';

import * as Styled from './complete-signup.style';
import { CompleteSignupForm } from './components/complete-signup-form';

const CompleteSignupContent: React.FC = () => {
  const { t } = useTranslation();
  const locale = useI18nLocale();

  const [claimToken] = useQueryParam(QueryParamOptions.ClaimToken);
  const email = useSignupClaim(claimToken);

  const { completeSignupAndLogin, isLoading, error } =
    useCompleteSignupAndLogin();

  const submitForm = ({ password, name }: CompleteSignupFormFields) =>
    !!claimToken &&
    completeSignupAndLogin(claimToken, {
      password,
      name,
      locale,
    });

  return (
    <>
      <Typography variant="h1" textAlign="center">
        {t('forms.complete-signup.title')}
      </Typography>

      <CompleteSignupForm
        email={email ?? ''}
        onSubmit={submitForm}
        isLoading={isLoading}
        error={error ?? undefined}
      />
    </>
  );
};

export const CompleteSignup: React.FC = () => {
  useTrackPageVisitEvent(WebPageEvents.CompleteSignup, undefined);
  const { t } = useTranslation();

  return (
    <Styled.Container>
      <Styled.Body>
        <Styled.BodyContent>
          <LoginLogo />

          <Suspense fallback={<PageLoader />}>
            <CompleteSignupContent />
          </Suspense>
        </Styled.BodyContent>
      </Styled.Body>

      <Styled.Footer>
        <Styled.TermsOfUse>
          {t('forms.terms-and-agreement.1')}
          <Styled.ExternalAnchor href="https://griply.app/eula" target="_blank">
            {t('forms.terms-and-agreement.terms-of-use')}
          </Styled.ExternalAnchor>
          {t('forms.terms-and-agreement.2')}
          <Styled.ExternalAnchor
            href="https://griply.app/privacy-policy"
            target="_blank"
          >
            {t('forms.terms-and-agreement.privacy-policy')}
          </Styled.ExternalAnchor>
          {t('forms.terms-and-agreement.3')}
        </Styled.TermsOfUse>
      </Styled.Footer>
    </Styled.Container>
  );
};
